import { Injectable } from '@angular/core';
import { IDraftData, TDraftStatus } from '@df/catalog/cms/cms-model.interface';
import { IModelDataV1 } from '@df/catalog/model';
import { uniqueId } from '@df/util/fn/unique-id';
import { NICmsApi } from '@icms/core/data/icms-api.interfaces';
import { ICmsDraft } from '@icms/core/resource/draft/icms-draft';
import { dateToString } from '@icms/core/util/fn/date-to-string';
import { ICmsModel } from '../../../core/resource/model/icms-model';
import { ICmsModelResource } from '../../../core/resource/model/icms-model.resource';

declare module '@df/catalog/resource' {
    interface IResources {
        draft: Resource<ICmsDraft, number>;
    }
}

@Injectable({
    providedIn: 'root'
})
export class ICmsDraftResource extends ICmsModelResource<ICmsDraft, number> {
    static override instance = ICmsDraft;
    readonly invalidatesUniversal = false;
    readonly invalidatesPrerender = false;

    createOnExisting<TData extends IModelDataV1, TModel extends ICmsModel<TData>>(
        model: TModel,
        draftData?: TData,
        data: Partial<IDraftData<TData>> = {}
    ): ICmsDraft<TData, TModel> {
        const id =
            Math.min(
                0,
                ...this.getAll()
                    .filter(i => i.id < 0)
                    .map(i => i.id)
            ) - 1;

        const draft = this.getCreate(id);
        const date = new Date();
        draft.setData({
            id,
            name: `Draft ${dateToString(date)}`,
            status: 'new',
            type: ICmsDraft.type,
            verbosity: 1,
            target: model.id,
            targetType: model.type,
            draftData: draftData || model.swapDataForDraft()!,
            createdAt: Date.now(),
            hash: uniqueId(''),
            ...data
        });

        return draft as ICmsDraft<TData, TModel>;
    }

    async loadFor(model: ICmsModel, statuses?: TDraftStatus[], sort?: object, limit?: number): Promise<ICmsDraft[]> {
        const filter = {
            target: model.id,
            targetType: model.type
        };

        if (statuses) {
            filter['status'] = { $in: statuses };
        }

        return this.find(
            {
                verbosity: 1,
                filter,
                sort,
                limit
            },
            { noCache: true }
        );
    }

    async loadActiveFor(model: ICmsModel): Promise<ICmsDraft[]> {
        return this.find({
            verbosity: 1,
            filter: {
                target: model.id,
                targetType: model.type
            }
        });
    }

    async saveAndPublish(drafts: ICmsDraft[]): Promise<NICmsApi.IPublishDraftResponse> {
        await Promise.all(drafts.map(draft => this.save(draft)));
        return this.publish(drafts);
    }

    /**
     * Saves iCms model
     */
    async publish(drafts: ICmsDraft[]): Promise<NICmsApi.IPublishDraftResponse> {
        const data: NICmsApi.IPublishDraftRequest = {
            ids: drafts.map(i => i.id)
        };

        drafts.forEach(draft => (draft.allowDataOverwrite = true));
        const result = await this.requestService.send<NICmsApi.IPublishDraftResponse>(undefined, {
            path: 'cms/publish-draft',
            data,
            api: 'node',
            token: true
        });
        drafts.forEach(draft => (draft.allowDataOverwrite = false));

        return result;
    }
    /**
     * Saves iCms model
     */
    async revert(drafts: ICmsDraft[]): Promise<NICmsApi.IPublishDraftResponse> {
        const data: NICmsApi.IPublishDraftRequest = {
            ids: drafts.map(i => i.id)
        };

        drafts.forEach(draft => (draft.allowDataOverwrite = true));
        const result = await this.requestService.send<NICmsApi.IPublishDraftResponse>(undefined, {
            path: 'cms/revert-draft',
            data,
            api: 'node',
            token: true
        });
        drafts.forEach(draft => (draft.allowDataOverwrite = false));

        return result;
    }
}
